import { ProfileProcessing } from "../pages/ProfileProcessing/ProfileProcessing";
import { MemberResponse } from "../types/MemberResponse";
import { PaginatedResponse } from "../types/PaginatedResponse";
import { api } from "./api/apiSlice";

export interface GetMembersParams {
  page?: number;
  size?: number;
  search?: string;
  membership_type_id?: string;
  country_id?: string;
  degree?: string;
}

export interface Member {
  id: string;
  full_name: string;
  nid: string;
  tin?: string;
  iba_membership_number: string;
  date_of_birth: string;
  blood_group: string;
  nationality?: string;
  membership_type_id?: string;
  club_facilities?: string[];
  contact_info?: {
    office_tel?: string;
    residence_tel?: string;
    fax?: string;
    mobile_tel?: string;
    email?: string;
    residential_address?: string;
    permanent_address?: string;
    mailing_address?: string;
  };
  professional_info?: {
    profession?: string;
    designation?: string;
    organization?: string;
    office_address?: string;
    category_area?: string | null;
    major_functional_area?: string | null;
    company_annual_turnover_bdt?: string;
    yearly_income?: string;
  };
  family_info?: {
    father_name?: string;
    mother_name?: string;
    spouse_name?: string;
    spouse_dob?: string | null;
    marital_status?: string;
    spouse_occupation?: string;
  };
  proposer?: {
    name?: string;
    membership_number?: string;
    membership_date?: string;
    known_years?: string;
  };
  seconder?: {
    name?: string;
    membership_ac_number?: string;
    date_of_pm?: string;
    known_years?: string;
    attachment_id?: string;
  };
  education?: any[];
  memberships?: any[];
  children?: any[];
  spouses?: any[];
  drivers?: any[];
  attachments?: {
    file_type: string;
    filename?: string;
    attachment_id: string;
    id?: string;
  }[];
  terms_and_conditions?: {
    name?: string;
    location_address?: string;
    day?: string;
    month?: string;
    year?: string;
  };
  payments?: {
    club_name?: string | null;
    membership_number?: string | null;
    date_of_membership?: string | null;
    membership_type?: string | null;
    is_additional_cost?: boolean;
    status?: string;
    description?: string;
    amount?: number;
    start_due_date?: string;
    end_due_date?: string;
    id?: string;
  }[];
}

export const memberpApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllMembers: builder.query<
      PaginatedResponse<MemberResponse>,
      GetMembersParams
    >({
      query: (params) => ({
        url: "members/",
        params: {
          page: params?.page || 1,
          size: params?.size || 10,
          ...(params?.membership_type_id && {
            membership_type_id: params.membership_type_id,
          }),
          ...(params?.country_id && { country_id: params.country_id }),
          ...(params?.degree && { degree: params.degree }),
          ...(params?.search && { search: params.search }),
        },
      }),
      providesTags: ["members"],
    }),
    getPendingMembers: builder.query<
      PaginatedResponse<MemberResponse>,
      GetMembersParams
    >({
      query: (params) => ({
        url: "members/pending",
        params: {
          page: params?.page || 1,
          size: params?.size || 10,
        },
      }),
      providesTags: ["pendingMembers"],
    }),
    getPublicMember: builder.query<
      any,
      {
        limit?: number;
        country_id?: string;
        membership_type_id?: string;
        degree?: string;
        founding_member?: string;
      }
    >({
      query: (filters = {}) => {
        const params = {
          ...(filters.limit && { limit: filters.limit }),
          ...(filters.country_id && { country_id: filters.country_id }),
          ...(filters.membership_type_id && {
            membership_type_id: filters.membership_type_id,
          }),
          ...(filters.degree && { degree: filters.degree }),
          ...{
            founding_member: filters.founding_member,
          },
        };
        console.log("Public Members API params:", params);
        return {
          url: `members/public`,
          params,
        };
      },
      providesTags: ["Country"],
    }),
    getMemberById: builder.query<MemberResponse, string | undefined>({
      query: (id) => `members/${id}`,
    }),
    getMemberListOption: builder.query({
      query: () => `members/list`,
    }),
    getUpcomingBirthdays: builder.query<any[], void>({
      query: () => `members/upcoming-birthdays`,
      providesTags: ["members"],
    }),
    getMemberProfileUpdatedDataById: builder.query<
      MemberResponse,
      string | undefined
    >({
      query: (id) => `members/profile-update-data/${id}`,
    }),
    getMemberByUserId: builder.query<MemberResponse, string | undefined | null>(
      {
        query: (id) => `members/user_by_id/${id}`,
      }
    ),
    addMember: builder.mutation<any, any>({
      query: (body) => ({
        url: `members/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["members"],
    }),
    updateMember: builder.mutation<any, any>({
      query: (body) => ({
        url: `members/${body?.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["members"],
    }),
    changeStatus: builder.mutation<any, any>({
      query: ({ data, id }) => ({
        url: `members/status-change/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["members"],
    }),
    updateDeceasedMember: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `members/deceased/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["members"],
    }),
    updateMemberProfile: builder.mutation<any, any>({
      query: (body) => ({
        url: `members/${body?.id}/profile-update`,
        method: "PUT",
        body,
      }),
    }),
    processMember: builder.mutation<
      Member,
      { id: string; data: ProfileProcessing }
    >({
      query: ({ id, data }) => ({
        url: `members/process/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["pendingMembers"],
    }),
    bulkUploadMembers: builder.mutation<any[], File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `members/bulk-upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["members"],
    }),
  }),
});

export const {
  useAddMemberMutation,
  useGetAllMembersQuery,
  useGetMemberByIdQuery,
  useUpdateMemberMutation,
  useGetMemberByUserIdQuery,
  useGetPendingMembersQuery,
  useProcessMemberMutation,
  useUpdateMemberProfileMutation,
  useGetMemberProfileUpdatedDataByIdQuery,
  useGetMemberListOptionQuery,
  useGetUpcomingBirthdaysQuery,
  useGetPublicMemberQuery,
  useChangeStatusMutation,
  useBulkUploadMembersMutation,
  useUpdateDeceasedMemberMutation,
} = memberpApi;
